// src/components/prescriptions/VisualBuilder.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getBranding, updateBranding } from "../../services/prescriptionsApi";
import ImageUpload from "./ImageUpload";
import PreviewPaper from "./PreviewPaper";
import api from "../../services/api";

/**
 * Montador visual do receituário/contrato com preview ao vivo.
 * Agora deixa claro que o visual é compartilhado entre Receituários e Contratos.
 */
const INITIAL_BRANDING = {
  clinic_name: "",
  doctor_name: "",
  specialty: "",
  cro_number: "",
  email: "",
  phone: "",
  address: "",
  logo_url: "",
  signature_url: "",
  primary_color: "#0ea5a4",
  font_family: "Arial",
  header_align: "center",

  // Layout pronto em imagem A4
  layout_mode: "builder", // "builder" (layout do sistema) ou "image" (usar imagem A4 pronta)
  layout_image_url: "",
  layout_image_header_mm: 60,
  layout_image_footer_mm: 40,

  // LOGO
  show_logo: true,
  logo_size_px: 140,

  // nome/CRO
  show_doctor_name: true,
  show_cro: true,

  // Marca d'água
  show_watermark: true,
  watermark_type: "svg", // svg | image
  watermark_image_url: "",
  watermark_opacity: 0.08,
  watermark_scale: 1.2,
  watermark_position: "center",

  // Rodapé
  show_footer_phone: true,
  show_footer_email: true,
  show_footer_address: true,
  show_footer_whatsapp: false,

  // Assinatura/Carimbo
  show_signature: false,
  show_stamp: false,
  // tamanho da assinatura (altura em px)
  signature_size_px: 72,
};

export default function VisualBuilder() {
  const [loading, setLoading] = useState(false);
  const [branding, setBranding] = useState(INITIAL_BRANDING);

  // NOVO: lista de profissionais e seleção para preencher nome/CRO
  const [professionals, setProfessionals] = useState([]);
  const [selectedProfessionalId, setSelectedProfessionalId] = useState("");

  // Carrega profissionais ao montar
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get("/clinic-settings");
        const profs = resp?.data?.professionals || [];
        setProfessionals(profs);
      } catch (err) {
        console.error("Erro ao carregar profissionais:", err);
      }
    })();
  }, []);

  // Carrega branding quando muda o profissional selecionado
  useEffect(() => {
    loadBranding(selectedProfessionalId);
  }, [selectedProfessionalId]);

  const loadBranding = async (profId) => {
    try {
      setLoading(true);
      const data = await getBranding(profId);
      if (data) {
        // Merge com o estado inicial para garantir que campos novos/faltantes não quebrem ou vazem
        setBranding({ ...INITIAL_BRANDING, ...data });
      } else {
        // Se não retornou nada, reseta para o inicial
        setBranding(INITIAL_BRANDING);
      }
    } catch (e) {
      console.error(e);
      toast.error("Erro ao carregar visual");
    } finally {
      setLoading(false);
    }
  };

  const onSave = async () => {
    try {
      setLoading(true);
      // Inclui o professional_id no payload
      const payload = {
        ...branding,
        professional_id: selectedProfessionalId || null
      };
      await updateBranding(payload);
      toast.success(
        "Configurações visuais salvas! (Aplicam-se a Receituários e Contratos)"
      );
    } catch (e) {
      console.error(e);
      toast.error("Erro ao salvar configurações visuais");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      {/* Controles */}
      <div className="bg-white border rounded-lg p-4 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Montar Receituários / Contratos</h2>
          <p className="text-xs text-gray-500 mt-1">
            As alterações de identidade visual (logo, cores, marca d’água, assinatura, rodapé etc.)
            são compartilhadas e afetam{" "}
            <span className="font-medium">Receituários e Contratos</span>.
          </p>
        </div>

        {/* Identificação */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm">Nome da clínica</span>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={branding.clinic_name}
              onChange={(e) =>
                setBranding({ ...branding, clinic_name: e.target.value })
              }
            />
          </label>

          {/* NOVO: seleção de profissional para carregar visual específico */}
          <label className="block">
            <span className="text-sm">Visual do Profissional</span>
            <select
              className="mt-1 w-full border rounded px-3 py-2 bg-white"
              value={selectedProfessionalId}
              onChange={(e) => setSelectedProfessionalId(e.target.value)}
            >
              <option value="">Padrão da Clínica (Geral)</option>
              {professionals.map((p) => (
                <option key={p.id || p.name} value={p.id || ""}>
                  {p.name || p.professional_name || "Profissional sem nome"}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">
              Selecione um profissional para configurar um visual exclusivo para ele.
              Se deixar em "Padrão", será usado para quem não tem visual próprio.
            </p>
          </label>

          <label className="block">
            <span className="text-sm">Nome do(a) dentista</span>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={branding.doctor_name}
              onChange={(e) =>
                setBranding({ ...branding, doctor_name: e.target.value })
              }
            />
          </label>
          <label className="block">
            <span className="text-sm">CRO</span>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={branding.cro_number}
              onChange={(e) =>
                setBranding({ ...branding, cro_number: e.target.value })
              }
            />
          </label>
          <label className="block">
            <span className="text-sm">Especialidade (opcional)</span>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={branding.specialty || ""}
              onChange={(e) =>
                setBranding({ ...branding, specialty: e.target.value })
              }
            />
          </label>
          <label className="block">
            <span className="text-sm">E-mail</span>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={branding.email}
              onChange={(e) =>
                setBranding({ ...branding, email: e.target.value })
              }
            />
          </label>
          <label className="block">
            <span className="text-sm">Telefone / WhatsApp</span>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={branding.phone}
              onChange={(e) =>
                setBranding({ ...branding, phone: e.target.value })
              }
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-sm">Endereço</span>
            <input
              className="mt-1 w-full border rounded px-3 py-2"
              value={branding.address}
              onChange={(e) =>
                setBranding({ ...branding, address: e.target.value })
              }
            />
          </label>
        </div>

        {/* Logo / assinatura */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="font-semibold">Logomarca e assinatura</div>
            <ImageUpload
              label="Escolher logomarca"
              value={branding.logo_url}
              onChange={(v) => setBranding({ ...branding, logo_url: v })}
            />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={branding.show_logo}
                onChange={(e) =>
                  setBranding({ ...branding, show_logo: e.target.checked })
                }
              />
              Mostrar logomarca
            </label>
            <label className="block">
              <span className="text-sm">
                Tamanho da logomarca ({branding.logo_size_px}px)
              </span>
              <input
                type="range"
                min="40"
                max="260"
                step="2"
                value={branding.logo_size_px}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    logo_size_px: parseInt(e.target.value, 10),
                  })
                }
              />
            </label>

            <div className="mt-2">
              <div className="font-semibold text-sm mb-1">
                Assinatura ou Carimbo+Assinatura do(a) dentista
              </div>
              <ImageUpload
                label="Upload da assinatura"
                value={branding.signature_url}
                onChange={(v) => setBranding({ ...branding, signature_url: v })}
              />
              <div className="mt-2 space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={branding.show_signature}
                    onChange={(e) =>
                      setBranding({
                        ...branding,
                        show_signature: e.target.checked,
                      })
                    }
                  />
                  Mostrar assinatura do(a) dentista
                </label>

                {/* Slider de tamanho da assinatura */}
                <div className="pl-5">
                  <label className="block">
                    <span className="text-xs text-gray-700">
                      Tamanho da assinatura ({branding.signature_size_px ?? 72}px)
                    </span>
                    <input
                      type="range"
                      min="40"
                      max="160"
                      step="2"
                      value={branding.signature_size_px ?? 72}
                      onChange={(e) =>
                        setBranding({
                          ...branding,
                          signature_size_px: parseInt(e.target.value, 10),
                        })
                      }
                      className="w-full"
                      disabled={!branding.show_signature}
                    />
                  </label>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={branding.show_stamp}
                    onChange={(e) =>
                      setBranding({ ...branding, show_stamp: e.target.checked })
                    }
                  />
                  Incluir carimbo (nome + CRO)
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="font-semibold">Título / alinhamento</div>
            <label className="block">
              <span className="text-sm">Alinhamento</span>
              <select
                className="mt-1 w-full border rounded px-3 py-2"
                value={branding.header_align}
                onChange={(e) =>
                  setBranding({ ...branding, header_align: e.target.value })
                }
              >
                <option value="left">Esquerda</option>
                <option value="center">Centralizado</option>
                <option value="right">Direita</option>
              </select>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={branding.show_doctor_name}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    show_doctor_name: e.target.checked,
                  })
                }
              />
              Mostrar nome do(a) dentista
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={branding.show_cro}
                onChange={(e) =>
                  setBranding({ ...branding, show_cro: e.target.checked })
                }
              />
              Mostrar CRO
            </label>
            <label className="block">
              <span className="text-sm">Fonte</span>
              <select
                className="mt-1 w-full border rounded px-3 py-2"
                value={branding.font_family}
                onChange={(e) =>
                  setBranding({ ...branding, font_family: e.target.value })
                }
              >
                <option>Arial</option>
                <option>Times New Roman</option>
                <option>Calibri</option>
                <option>Georgia</option>
                <option>Verdana</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm">Cor primária</span>
              <input
                type="color"
                className="mt-1 h-10 w-24 border rounded"
                value={branding.primary_color}
                onChange={(e) =>
                  setBranding({ ...branding, primary_color: e.target.value })
                }
              />
            </label>
          </div>
        </div>

        {/* Layout pronto com imagem A4 */}
        <div className="space-y-3">
          <div className="font-semibold">Layout pronto (papel A4 em imagem)</div>
          <p className="text-xs text-gray-500">
            Se você já tem um receituário/contrato pronto em JPG ou PNG no formato A4,
            envie o arquivo abaixo e escolha usar esse layout. Nesse modo só serão exibidos
            carimbo e assinatura sobre a imagem.
          </p>

          <label className="block">
            <span className="text-sm">Modo de layout</span>
            <select
              className="mt-1 w-full border rounded px-3 py-2"
              value={branding.layout_mode || "builder"}
              onChange={(e) =>
                setBranding({ ...branding, layout_mode: e.target.value })
              }
            >
              <option value="builder">
                Usar construtor (cabeçalho/rodapé do sistema)
              </option>
              <option value="image">Usar imagem A4 pronta</option>
            </select>
          </label>

          {branding.layout_mode === "image" && (
            <div className="space-y-3">
              <ImageUpload
                label="Upload de layout A4 (JPG/PNG)"
                value={branding.layout_image_url}
                onChange={(v) =>
                  setBranding({ ...branding, layout_image_url: v })
                }
                accept="image/*"
              />
              <div className="text-xs text-gray-500">
                Dica: use imagem em proporção A4 (por ex. 2480×3508 px). Ela
                será usada como fundo de cada página.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm">
                    Área reservada superior (cabeçalho) em mm
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={branding.layout_image_header_mm ?? 0}
                    onChange={(e) =>
                      setBranding({
                        ...branding,
                        layout_image_header_mm:
                          Number(e.target.value) || 0,
                      })
                    }
                  />
                </label>
                <label className="block">
                  <span className="text-sm">
                    Área reservada inferior (rodapé) em mm
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    className="mt-1 w-full border rounded px-3 py-2"
                    value={branding.layout_image_footer_mm ?? 0}
                    onChange={(e) =>
                      setBranding({
                        ...branding,
                        layout_image_footer_mm:
                          Number(e.target.value) || 0,
                      })
                    }
                  />
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Marca d'água */}
        <div className="space-y-3">
          <div className="font-semibold">Marca d'água</div>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={branding.show_watermark}
              onChange={(e) =>
                setBranding({
                  ...branding,
                  show_watermark: e.target.checked,
                })
              }
            />
            Exibir marca d'água
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm">Tipo</span>
              <select
                className="mt-1 w-full border rounded px-3 py-2"
                value={branding.watermark_type}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    watermark_type: e.target.value,
                  })
                }
              >
                <option value="svg">Padrão (desenho de dente)</option>
                <option value="image">Imagem personalizada</option>
              </select>
            </label>

            <label className="block">
              <span className="text-sm">Posição</span>
              <select
                className="mt-1 w-full border rounded px-3 py-2"
                value={branding.watermark_position}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    watermark_position: e.target.value,
                  })
                }
              >
                <option value="center">Central</option>
                <option value="bottom-right">Inferior direita</option>
              </select>
            </label>
          </div>

          {branding.watermark_type === "image" && (
            <div className="space-y-2">
              <ImageUpload
                label="Upload da marca d'água"
                value={branding.watermark_image_url}
                onChange={(v) =>
                  setBranding({ ...branding, watermark_image_url: v })
                }
              />
              <div className="text-xs text-gray-500">
                Dica: use PNG/SVG com fundo transparente para melhor resultado.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm">
                Opacidade ({Math.round(branding.watermark_opacity * 100)}%)
              </span>
              <input
                type="range"
                min="0"
                max="0.3"
                step="0.01"
                value={branding.watermark_opacity}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    watermark_opacity: parseFloat(e.target.value),
                  })
                }
              />
            </label>
            <label className="block">
              <span className="text-sm">Tamanho</span>
              <input
                type="range"
                min="0.6"
                max="2"
                step="0.05"
                value={branding.watermark_scale}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    watermark_scale: parseFloat(e.target.value),
                  })
                }
              />
            </label>
          </div>
        </div>

        {/* Rodapé */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="font-semibold">Rodapé</div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={branding.show_footer_phone}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    show_footer_phone: e.target.checked,
                  })
                }
              />
              Mostrar telefone
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={branding.show_footer_whatsapp}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    show_footer_whatsapp: e.target.checked,
                  })
                }
              />
              Destacar WhatsApp
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={branding.show_footer_email}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    show_footer_email: e.target.checked,
                  })
                }
              />
              Mostrar e-mail
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={branding.show_footer_address}
                onChange={(e) =>
                  setBranding({
                    ...branding,
                    show_footer_address: e.target.checked,
                  })
                }
              />
              Mostrar endereço
            </label>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={onSave}
            className="px-4 py-2 bg-purple-600 text-white rounded-md"
            disabled={loading}
          >
            {loading ? "Salvando..." : "Salvar configurações"}
          </button>
        </div>
      </div>

      {/* Pré-visualização ao vivo */}
      <div className="bg-white border rounded-lg p-2">
        <PreviewPaper
          branding={branding}
          content={{
            title: "Exemplo",
            guidance_text: "Este é um preview do papel A4 com seu visual.",
          }}
          variant="template"
        />
      </div>
    </div>
  );
}
